const ACCESS_TOKEN_STORAGE_KEY = 'ponzinomics_access_token';
const REFRESH_TOKEN_STORAGE_KEY = 'ponzinomics_refresh_token';
const BUILDER_LAUNCH_SOURCE_QUERY_PARAM = 'launch';
const BUILDER_LAUNCH_SOURCE_DASHBOARD = 'dashboard';

export const BUILDER_AUTH_HANDOFF_MESSAGE_TYPE = 'ponzinomics-builder-auth-handoff';
export const BUILDER_AUTH_HANDOFF_ACK_MESSAGE_TYPE = 'ponzinomics-builder-auth-handoff:ack';

export interface BuilderLaunchParams {
  projectId?: string;
  siteId?: string;
}

interface BuilderAuthHandoffPayload {
  accessToken?: string;
  refreshToken?: string;
  projectId?: string;
}

function joinBaseUrl(baseUrl: string, path: string): string {
  const url = new URL(baseUrl);
  const normalizedBasePath = url.pathname.replace(/\/$/, '');
  url.pathname = `${normalizedBasePath}/${path.replace(/^\//, '')}`.replace(/\/+/g, '/');
  url.search = '';
  url.hash = '';
  return url.toString();
}

function readStoredDashboardSession(projectId?: string): BuilderAuthHandoffPayload {
  if (typeof window === 'undefined') {
    return {};
  }

  const payload: BuilderAuthHandoffPayload = {};
  const accessToken = window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  const refreshToken = window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);

  if (accessToken) {
    payload.accessToken = accessToken;
  }

  if (refreshToken) {
    payload.refreshToken = refreshToken;
  }

  if (projectId) {
    payload.projectId = projectId;
  }

  return payload;
}

function getBuilderBaseUrl(): string {
  const url = import.meta.env.VITE_BUILDER_URL?.replace(/\/$/, '');
  if (!url) {
    throw new Error('VITE_BUILDER_URL is required');
  }
  return url;
}

export function buildBuilderEditorUrl(params: BuilderLaunchParams = {}): string {
  const targetUrl = new URL(joinBaseUrl(getBuilderBaseUrl(), 'editor'));
  targetUrl.searchParams.set(BUILDER_LAUNCH_SOURCE_QUERY_PARAM, BUILDER_LAUNCH_SOURCE_DASHBOARD);

  if (params.projectId) {
    targetUrl.searchParams.set('project', params.projectId);
  }

  if (params.siteId) {
    targetUrl.searchParams.set('site', params.siteId);
  }

  return targetUrl.toString();
}

export function launchBuilderWindow(params: BuilderLaunchParams = {}): Window | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const targetUrl = buildBuilderEditorUrl(params);
  const builderWindow = window.open(targetUrl, '_blank');

  if (!builderWindow) {
    return null;
  }

  const payload = readStoredDashboardSession(params.projectId);
  if (!payload.accessToken && !payload.projectId) {
    return builderWindow;
  }

  const builderOrigin = new URL(targetUrl).origin;
  let attempts = 0;
  let intervalId = 0;

  const cleanup = (): void => {
    window.clearInterval(intervalId);
    window.removeEventListener('message', handleAck);
  };

  const postHandoff = (): void => {
    if (builderWindow.closed) {
      cleanup();
      return;
    }

    builderWindow.postMessage(
      {
        type: BUILDER_AUTH_HANDOFF_MESSAGE_TYPE,
        payload,
      },
      builderOrigin
    );

    attempts += 1;
    if (attempts >= 20) {
      cleanup();
    }
  };

  const handleAck = (event: MessageEvent): void => {
    if (event.origin !== builderOrigin) {
      return;
    }

    if ((event.data as { type?: string } | null)?.type !== BUILDER_AUTH_HANDOFF_ACK_MESSAGE_TYPE) {
      return;
    }

    cleanup();
  };

  window.addEventListener('message', handleAck);
  postHandoff();
  intervalId = window.setInterval(postHandoff, 250);

  return builderWindow;
}
